/**
 * RetailConfigurator — UI of the rule-driven configurator.
 *
 * The browser owns no business logic : every price, discount, rule and total is
 * computed by `ioi_retail_suite.api.resolve` so the desk, the portal and a PDF
 * always agree on the number.
 */

/* global frappe */

class RetailConfigurator {
	constructor(container, options = {}) {
		this.root = typeof container === "string" ? document.querySelector(container) : container;
		this.options = Object.assign({ channel: "Desk", model: null }, options);
		this.state = {
			models: [],
			model: null,
			payload: null,
			selections: {},
			quantities: {},
			multiplier: 1,
			resolved: null,
			customer: null,
			journal: null,
			busy: false,
		};
	}

	async mount() {
		this.root.innerHTML = TEMPLATE;
		this.el = {
			rail: this.root.querySelector("#rc-rail"),
			scene: this.root.querySelector("#rc-scene"),
			lines: this.root.querySelector("#rc-lines"),
			totals: this.root.querySelector("#rc-totals"),
			meta: this.root.querySelector("#rc-meta"),
			messages: this.root.querySelector("#rc-messages"),
			customer: this.root.querySelector("#rc-customer"),
			journal: this.root.querySelector("#rc-journal"),
			result: this.root.querySelector("#rc-result"),
		};
		this._bind();

		this.state.models = await frappe.xcall("ioi_retail_suite.api.get_models", {
			channel: this.options.channel,
		});
		if (!this.state.models.length) {
			this.el.scene.innerHTML = `<div class="rc-empty">Aucun modèle configurable actif.</div>`;
			return this;
		}
		await this.loadCustomers();
		await this.selectModel(this.options.model || this.state.models[0].name);
		return this;
	}

	async loadCustomers() {
		const rows = await frappe.xcall("ioi_retail_suite.api.get_customers", { limit: 200 });
		this.el.customer.innerHTML = (rows || [])
			.map((r) => `<option value="${r.name}">${frappe.utils.escape_html(r.full_name || r.name)}</option>`)
			.join("");
		this.state.customer = this.el.customer.value;
	}

	async selectModel(name) {
		this.state.model = name;
		this.state.payload = await frappe.xcall("ioi_retail_suite.api.get_model", { model: name });
		this.el.journal.innerHTML = (this.state.payload.journals || [])
			.map((j) => `<option value="${j.journal}">${frappe.utils.escape_html(j.label)}</option>`)
			.join("");
		this.state.journal = this.el.journal.value;
		this.state.selections = this.defaults();
		this.state.quantities = {};
		this.state.multiplier = 1;
		await this.refresh();
	}

	/** Pre-select the flagged defaults, then the first option of every mandatory step. */
	defaults() {
		const selections = {};
		for (const step of this.state.payload.steps) {
			const flagged = step.options.filter((o) => o.is_default).map((o) => o.option_code);
			if (step.selection_type === "Multiple") {
				if (flagged.length) selections[step.step_code] = flagged;
			} else if (flagged.length) {
				selections[step.step_code] = flagged[0];
			} else if (!step.optional && step.options.length) {
				selections[step.step_code] = step.options[0].option_code;
			}
		}
		return selections;
	}

	async refresh() {
		if (this.state.busy) return;
		this.state.busy = true;
		try {
			this.state.resolved = await frappe.xcall("ioi_retail_suite.api.resolve", {
				model: this.state.model,
				selections: JSON.stringify(this.state.selections),
				quantities: JSON.stringify(this.state.quantities),
				multiplier: this.state.multiplier,
			});
			this.state.selections = this.state.resolved.selections;
			this.render();
		} finally {
			this.state.busy = false;
		}
	}

	// ------------------------------------------------------------ rendering
	render() {
		this.renderRail();
		this.renderScene();
		this.renderSummary();
	}

	renderRail() {
		this.el.rail.innerHTML = this.state.models
			.map(
				(m) => `<button data-model="${m.name}" class="${m.name === this.state.model ? "on" : ""}">
					<span class="em">${m.icon || "▪"}</span>${frappe.utils.escape_html(m.title)}</button>`
			)
			.join("");
		this.el.rail.querySelectorAll("[data-model]").forEach((node) => {
			node.onclick = () => this.selectModel(node.dataset.model);
		});
	}

	renderScene() {
		const payload = this.state.payload;
		const excluded = (this.state.resolved && this.state.resolved.rules.excluded) || {};
		const tiers = payload.tiers || [];

		const steps = payload.steps
			.map((step, index) => {
				const hidden = excluded[step.step_code] || [];
				const options = step.options.filter((o) => !hidden.includes(o.option_code));
				const current = this.state.selections[step.step_code];
				const chosen = Array.isArray(current) ? current : current ? [current] : [];

				const cards = options
					.map((option) => {
						const on = chosen.includes(option.option_code);
						const stock = option.stock || 0;
						return `<div class="rc-opt${on ? " on" : ""}" data-step="${step.step_code}" data-option="${option.option_code}">
							${option.image ? `<img src="${option.image}" alt="">` : `<div class="rc-noimg"></div>`}
							<div class="nm">${frappe.utils.escape_html(option.label || option.description || option.item)}</div>
							<div class="cd">${option.item}</div>
							<div class="pr">${money(option.price)}</div>
							<div class="stk ${stock > 50 ? "ok" : "low"}">${stock > 50 ? "En stock" : "Stock limité"} · ${Math.round(stock)}</div>
						</div>`;
					})
					.join("");

				const qty =
					step.selection_type === "Quantity"
						? `<div class="rc-qty">
							<input type="range" min="${step.min_qty}" max="${step.max_qty}"
								value="${this.state.quantities[step.step_code] ?? step.default_qty}"
								data-qty="${step.step_code}">
							<span class="val">${this.state.quantities[step.step_code] ?? step.default_qty} ×</span>
						</div>`
						: "";

				return `<div class="rc-step">
					<h4><i>${index + 1}</i>${frappe.utils.escape_html(step.label)}${step.optional ? " <em>(optionnel)</em>" : ""}</h4>
					${step.hint ? `<p class="rc-hint">${frappe.utils.escape_html(step.hint)}</p>` : ""}
					<div class="rc-grid">${cards}</div>${qty}</div>`;
			})
			.join("");

		this.el.scene.innerHTML = `
			<div class="rc-head">
				<h2>${payload.model.icon || ""} ${frappe.utils.escape_html(payload.model.title)}</h2>
				<span>${payload.steps.length} étapes · prix catalogue ioi en direct</span>
			</div>
			${payload.model.description ? `<p class="rc-note">${frappe.utils.escape_html(payload.model.description)}</p>` : ""}
			<div class="rc-step">
				<h4><i>×</i>Multiplicateur — pièces ou postes identiques</h4>
				<div class="rc-qty">
					<input type="range" min="1" max="60" value="${this.state.multiplier}" id="rc-mult">
					<span class="val">${this.state.multiplier} ×</span>
				</div>
				<div class="rc-tiers">${tiers
					.map((t) => `<span class="rc-tier${this.state.multiplier >= t.qty ? " on" : ""}">≥ ${t.qty} → −${t.pct}%</span>`)
					.join("")}</div>
			</div>
			${steps}`;

		this.el.scene.querySelectorAll(".rc-opt").forEach((node) => {
			node.onclick = () => this.toggle(node.dataset.step, node.dataset.option);
		});
		this.el.scene.querySelectorAll("[data-qty]").forEach((node) => {
			node.oninput = () => {
				this.state.quantities[node.dataset.qty] = Number(node.value);
				this.refresh();
			};
		});
		const mult = this.el.scene.querySelector("#rc-mult");
		if (mult)
			mult.oninput = () => {
				this.state.multiplier = Number(mult.value);
				this.refresh();
			};
	}

	renderSummary() {
		const resolved = this.state.resolved;
		if (!resolved) return;
		const lines = resolved.lines;

		this.el.lines.innerHTML = lines.length
			? lines
					.map(
						(line) => `<div class="rc-line">
					${line.image ? `<img src="${line.image}">` : `<div class="rc-noimg sm"></div>`}
					<div class="t"><b>${frappe.utils.escape_html(line.description)}</b>
						<span>${line.qty} × ${money(line.unit_price)}${line.discount_pct ? ` · −${line.discount_pct}%` : ""}${line.source === "Rule" ? " · règle" : ""}</span></div>
					<div class="a">${money(line.amount)}</div></div>`
					)
					.join("")
			: `<div class="rc-empty">Sélectionnez vos éléments : le chiffrage se met à jour en direct.</div>`;

		const t = resolved.totals;
		this.el.totals.innerHTML = `
			<div class="row"><span>Sous-total HTVA</span><b>${money(t.total_gross)}</b></div>
			<div class="row disc"><span>Remise volume</span><b>−${money(t.total_discount)}</b></div>
			<div class="row"><span>TVA ${t.vat_rate}%</span><b>${money(t.total_vat)}</b></div>
			<div class="row grand"><span>Total TVAC</span><b>${money(t.total_incl)}</b></div>`;

		this.el.meta.innerHTML = `
			<div><span>Marge estimée</span><b>${t.estimated_margin} %</b></div>
			<div><span>Délai</span><b>${t.lead_time_days} j</b></div>
			<div><span>CO₂ évité</span><b>${t.co2_estimate} kg</b></div>`;

		this.el.messages.innerHTML = (resolved.rules.messages || [])
			.map((m) => `<div class="rc-msg rc-${m.severity.toLowerCase()}">${frappe.utils.escape_html(m.text)}</div>`)
			.join("");
	}

	// -------------------------------------------------------------- actions
	toggle(stepCode, optionCode) {
		const step = this.state.payload.steps.find((s) => s.step_code === stepCode);
		const current = this.state.selections[stepCode];
		if (step.selection_type === "Multiple") {
			const list = Array.isArray(current) ? [...current] : [];
			const at = list.indexOf(optionCode);
			if (at >= 0) list.splice(at, 1);
			else list.push(optionCode);
			this.state.selections[stepCode] = list;
		} else if (current === optionCode && step.optional) {
			this.state.selections[stepCode] = null;
		} else {
			this.state.selections[stepCode] = optionCode;
		}
		this.refresh();
	}

	async push(kind) {
		const resolved = this.state.resolved;
		if (!resolved || !resolved.lines.length) {
			this.el.result.innerHTML = `<span class="ko">La configuration est vide.</span>`;
			return;
		}
		if (resolved.rules.blocking) {
			this.el.result.innerHTML = `<span class="ko">Des conflits bloquants doivent être levés.</span>`;
			return;
		}
		this.el.result.textContent = "Envoi vers ioi…";
		try {
			const document_ = await frappe.xcall("ioi_retail_suite.api.quick_document", {
				model: this.state.model,
				kind,
				selections: JSON.stringify(this.state.selections),
				quantities: JSON.stringify(this.state.quantities),
				multiplier: this.state.multiplier,
				customer: this.el.customer.value,
				journal: this.el.journal.value,
			});
			this.el.result.innerHTML = `<span class="ok">${kind === "quote" ? "Devis créé" : "Commande créée"} :</span>
				<a href="${document_.route}" target="_blank">${frappe.utils.escape_html(document_.name)}</a>
				— ${money(document_.total_tvac || 0)} TVAC
				· <a href="/app/retail-configuration/${document_.configuration}" target="_blank">configuration</a>`;
		} catch (error) {
			this.el.result.innerHTML = `<span class="ko">Échec :</span> ${frappe.utils.escape_html(
				(error.message || String(error)).slice(0, 240)
			)}`;
		}
	}

	_bind() {
		this.root.querySelector("#rc-quote").onclick = () => this.push("quote");
		this.root.querySelector("#rc-order").onclick = () => this.push("order");
		this.root.querySelector("#rc-reset").onclick = () => {
			this.state.selections = this.defaults();
			this.state.quantities = {};
			this.state.multiplier = 1;
			this.el.result.innerHTML = "";
			this.refresh();
		};
	}
}

function money(value) {
	return (
		Number(value || 0).toLocaleString("fr-BE", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}) + " €"
	);
}

const TEMPLATE = `
<div class="rc">
	<nav id="rc-rail" class="rc-rail"></nav>
	<main id="rc-scene" class="rc-main"></main>
	<aside class="rc-side">
		<h3>Votre configuration</h3>
		<div id="rc-lines" class="rc-lines"></div>
		<div id="rc-totals" class="rc-totals"></div>
		<div id="rc-meta" class="rc-metas"></div>
		<div id="rc-messages"></div>
		<label class="rc-field"><span>Client</span><select id="rc-customer"></select></label>
		<label class="rc-field"><span>Journal de vente</span><select id="rc-journal"></select></label>
		<div class="rc-actions">
			<button id="rc-reset" class="rc-btn ghost">Réinitialiser</button>
			<button id="rc-quote" class="rc-btn">Créer le devis</button>
			<button id="rc-order" class="rc-btn solid">Créer la commande</button>
		</div>
		<div id="rc-result" class="rc-result"></div>
	</aside>
</div>`;

if (typeof window !== "undefined") window.RetailConfigurator = RetailConfigurator;
